import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { OfcBoard } from '@/components/chinesepoker/OfcBoard';
import { DrawTray } from '@/components/chinesepoker/DrawTray';
import { useActiveQcpMatch } from '@/contexts/ActiveMatchContext';
import { useOfcPlacement } from '@/hooks/useOfcPlacement';
import { useHaptics } from '@/hooks/useHaptics';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveMatch } from '@/store/activeMatch.slice';
import type { Card, Row, Seat } from '@/lib/qcp/protocol';

/**
 * Live Chinese Poker (Pineapple OFC) match screen.
 *
 * Composes:
 *  - useActiveQcpMatch — reads the match socket from ActiveMatchProvider
 *    (the socket lives above the route tree, not per-screen)
 *  - useOfcPlacement — local pending-placement state for one round
 *  - OfcBoard / DrawTray — OFC UI components (Task 4)
 *
 * Layout (portrait, max-w-[520px]):
 *  Top    — opponent board (compact, placed cards only — anti-leak) + draw/discard counts
 *  Middle — clock pills, turn indicator, commit hash
 *  Bottom — my board + draw tray, Confirm button, Resign button
 *
 * Anti-leak: opponent draw cards (private state) are NEVER rendered; only
 * `drawCount` and `discardCount` from the masked opponents projection are shown.
 */
export function ChinesePokerMatch() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const haptics = useHaptics();

  const { t } = useTranslation('chinesepoker');

  const active = useAppSelector((s) => s.activeMatch.current);
  const dispatch = useAppDispatch();

  // Fallback deep-link/reload: if we land on the match route without an
  // activeMatch for this id (e.g. WebView reload), register it from
  // location.state/:id so the provider connects. Mount-only bootstrap — it
  // must NOT react to later active.matchId changes, or the provider's
  // central teardown clearing the slice on natural match end would re-fire
  // this effect and resurrect the just-ended match.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (id && active?.matchId !== id) {
      const vsBot = (location.state as { vsBot?: boolean } | null)?.vsBot;
      dispatch(setActiveMatch({ game: 'qcp', matchId: id, vsBot, startedAt: Date.now() }));
    }
    // Bootstrap once on cold mount only — deps intentionally empty.
  }, []);

  const m = useActiveQcpMatch();

  // ── Seat + Fantasyland identity (drives the asymmetric FL flow) ─────────
  const mySeat = m.mySeat;
  const oppSeat: Seat | null = mySeat === 'A' ? 'B' : mySeat === 'B' ? 'A' : null;
  // This seat in FL this hand → it sets all 13 at once from a 14-17 card draw.
  const myFlCount = mySeat ? m.flCardCount[mySeat] : 0;
  const myFl = mySeat ? m.inFantasyland[mySeat] : false;
  const oppFl = oppSeat ? m.inFantasyland[oppSeat] : false;

  // ── Placement state (local, per round / one-shot in FL) ────────────────
  const round = m.state?.public.round ?? 0;
  const placement = useOfcPlacement(round, m.draw, myFl ? myFlCount : 0);

  // A pending card "picked up" on the board (key `${r}${s}`) to move/remove it
  // this hand; null = nothing picked. Reset on round change / confirm / tray-pick.
  const [pickedBoardKey, setPickedBoardKey] = useState<string | null>(null);
  // Per-row display order override (card keys). Lets the player rearrange cards
  // within a row — INCLUDING previously-placed (committed) cards. Purely cosmetic:
  // a row is scored as a set, so display order never affects the hand or what's
  // submitted. Reset each round.
  const [rowOrder, setRowOrder] = useState<Record<Row, string[]>>({ front: [], middle: [], back: [] });
  useEffect(() => {
    setPickedBoardKey(null);
    setRowOrder({ front: [], middle: [], back: [] });
  }, [round]);

  // ── Clock: 1 s tick decrementing the on-move seat's banked time ──────
  const [displayClocks, setDisplayClocks] = useState<Record<Seat, number>>({ A: 0, B: 0 });
  const clockOwnerRef = useRef<Seat | null>(null);
  const lastTickRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!m.clocks) return;
    // Sync from server push. The server banks time in MILLISECONDS
    // (ClockManager.bankMs); the display + the 1 s tick below work in SECONDS,
    // so convert here — otherwise the clock reads e.g. "1939:11" (ms shown as s).
    setDisplayClocks({ A: m.clocks.banked.A / 1000, B: m.clocks.banked.B / 1000 });
    clockOwnerRef.current = m.clocks.owner;
    lastTickRef.current = Date.now();
  }, [m.clocks]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const owner = clockOwnerRef.current;
      if (!owner) return;
      const now = Date.now();
      const elapsed = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setDisplayClocks((prev) => ({
        ...prev,
        [owner]: Math.max(0, prev[owner] - elapsed),
      }));
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  // ── Navigate to result screen once the SET (multi-hand match) ends ──────
  // The terminal signal is match:result (the multi-hand "set" end), NOT the
  // legacy per-hand match:ended. ChinesePokerResult consumes { matchResult,
  // mySeat } from router state.
  useEffect(() => {
    if (m.matchResult && id) {
      navigate(`/chinesepoker/result/${id}`, {
        state: { matchResult: m.matchResult, mySeat: m.mySeat },
        replace: true,
      });
    }
  }, [m.matchResult, id, navigate, m.mySeat]);

  // ── Guard: wait for first state + seat identity ─────────────────────────
  const ready = mySeat !== null && m.state !== null;

  // ── Placement guard ────────────────────────────────────────────────────
  const isMyTurn = ready && m.state!.public.turn === mySeat;
  const isPlacingPhase = ready && m.state!.public.phase === 'placing';
  // Cards already committed to MY board (server-authoritative count).
  const myBoardPlaced =
    ready && mySeat
      ? m.state!.public.boards[mySeat].front.length +
        m.state!.public.boards[mySeat].middle.length +
        m.state!.public.boards[mySeat].back.length
      : 0;
  // FL: I set all 13 at once (not turn-gated) until my board is complete.
  // Normal: place on my turn during the placing phase.
  const showPlaceUI = isPlacingPhase && (myFl ? myBoardPlaced < 13 : isMyTurn);

  // ── Confirm handler ────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!placement.isComplete) return;
    haptics.impact('medium');
    if (myFl) {
      // Fantasyland: submit the whole grouped board (3/5/5) + the leftover
      // discards (the 1-4 cards still in the draw) in a single place:fl move.
      const flBoard = {
        front: placement.pending.filter((p) => p.row === 'front').map((p) => p.card),
        middle: placement.pending.filter((p) => p.row === 'middle').map((p) => p.card),
        back: placement.pending.filter((p) => p.row === 'back').map((p) => p.card),
      };
      const placedSet = new Set(placement.pending.map((p) => `${p.card.r}${p.card.s}`));
      const flDiscards = m.draw.filter((c) => !placedSet.has(`${c.r}${c.s}`));
      m.placeFl(flBoard, flDiscards);
    } else {
      m.place(placement.pending.map(({ card, row }) => ({ card, row })), placement.discard);
    }
    placement.clear();
    setPickedBoardKey(null);
  };

  // ── Clock formatter: m:ss ─────────────────────────────────────────────
  function formatClock(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${rem.toString().padStart(2, '0')}`;
  }

  // ── Net formatter: signed cumulative points (+3 / -2 / +0) ────────────
  const fmtNet = (n: number): string => `${n >= 0 ? '+' : ''}${n}`;
  const netClass = (n: number): string =>
    n > 0 ? 'text-emerald-400' : n < 0 ? 'text-rose-400' : 'text-fg-muted';

  // ── Opponent draw/discard counts (anti-leak: no cards, only counts) ───
  const oppCounts = oppSeat ? m.state?.opponents[oppSeat] : undefined;

  // ── Commit hash (short 8-char preview for provably-fair display) ───────
  const shortHash = m.commitHash ? m.commitHash.slice(0, 8) : null;

  // ── Empty board for loading state ─────────────────────────────────────
  const emptyBoard = { front: [], middle: [], back: [] };

  // ──────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────

  // Connecting / seat-discovery loader
  if (!ready) {
    return (
      <div
        className="relative mx-auto flex max-w-[520px] flex-col items-center justify-center gap-3 px-3 py-12"
        style={{ paddingTop: 'calc(var(--tg-viewport-safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <span className="h-3 w-3 animate-ping rounded-full bg-brand" />
        <p className="text-sm font-medium text-fg-muted">
          {m.connected ? t('match.settingUp') : t('match.connecting')}
        </p>
        {m.error && (
          <p className="rounded-xl bg-danger/15 px-4 py-2 text-sm text-danger">
            {m.error.message}
          </p>
        )}
      </div>
    );
  }

  const publicState = m.state!.public;

  // ── Optimistic placement ────────────────────────────────────────────────
  // Merge un-submitted pending placements into MY board so a card visibly moves
  // to its row the instant you tap (not only after Confirm), and drop placed
  // cards out of the draw tray. The placement hook indexes into the ORIGINAL
  // m.draw, so we tag each remaining card with its draw index and map tray
  // position → draw index when wiring the tray below.
  const cardKey = (c: { r: number; s: string }) => `${c.r}${c.s}`;
  const serverBoard = publicState.boards[mySeat];
  // Real cards in a row = committed (server) + this hand's pending placements.
  const rawRow = (row: Row): Card[] => [
    ...serverBoard[row],
    ...placement.pending.filter((p) => p.row === row).map((p) => p.card),
  ];
  // Apply the per-row display-order override: cards named in `order` first (in
  // that order), then any not listed (e.g. just-placed) appended.
  const orderRow = (cards: Card[], order: string[]): Card[] => {
    if (order.length === 0) return cards;
    const byKey = new Map(cards.map((c) => [cardKey(c), c] as const));
    const ordered: Card[] = [];
    for (const k of order) {
      const c = byKey.get(k);
      if (c) { ordered.push(c); byKey.delete(k); }
    }
    for (const c of cards) if (byKey.has(cardKey(c))) ordered.push(c);
    return ordered;
  };
  const myDisplayBoard = {
    front: orderRow(rawRow('front'), rowOrder.front),
    middle: orderRow(rawRow('middle'), rowOrder.middle),
    back: orderRow(rawRow('back'), rowOrder.back),
  };
  const placedKeys = new Set(placement.pending.map((p) => cardKey(p.card)));
  const remainingDraw = m.draw
    .map((card, idx) => ({ card, idx }))
    .filter(({ card }) => !placedKeys.has(cardKey(card)));
  const selectedTrayPos = remainingDraw.findIndex((r) => r.idx === placement.selectedIndex);

  return (
    <div
      className="relative mx-auto flex max-w-[520px] flex-col gap-3 px-3 pb-6"
      style={{ paddingTop: 'calc(var(--tg-viewport-safe-area-inset-top, 0px) + 0.75rem)' }}
    >
      {/* ── Set header: hand counter + running cumulative net ──────────── */}
      <div className="flex items-center justify-between rounded-xl border border-border bg-bg-secondary px-3 py-2">
        <span className="text-sm font-semibold text-fg">
          {t('hand.counter', { n: m.handNumber || 1 })}
        </span>
        <div className="flex items-center gap-3 text-xs font-medium tabular-nums">
          <span className={netClass(mySeat ? m.runningNet[mySeat] : 0)}>
            {t('hand.yourScore')} {fmtNet(mySeat ? m.runningNet[mySeat] : 0)}
          </span>
          <span className={netClass(oppSeat ? m.runningNet[oppSeat] : 0)}>
            {t('hand.opponentScore')} {fmtNet(oppSeat ? m.runningNet[oppSeat] : 0)}
          </span>
        </div>
      </div>

      {/* ── Error toast (transient, auto-cleared by hook after 4 s) ──────── */}
      {m.error && (
        <div className="rounded-xl bg-danger/15 px-4 py-2 text-center text-sm font-medium text-danger">
          {m.error.message}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* TOP: Opponent board (compact, placed cards only — anti-leak)      */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div className="flex flex-col gap-1">
        {/* Opponent header: seat label + FL status OR draw/discard counts */}
        <div className="flex items-center gap-2 px-1">
          <span className="text-xs font-semibold text-fg-muted">{t('match.opponent')}</span>
          {oppFl ? (
            <span className="rounded-md bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning">
              ✦ {t('hand.opponentInFantasyland', { count: oppSeat ? m.flCardCount[oppSeat] : 0 })}
            </span>
          ) : (
            oppCounts !== undefined && (
              <span className="text-[11px] text-fg-muted">
                {t('match.draws', { n: oppCounts.drawCount })} · {t('match.discards', { n: oppCounts.discardCount })}
              </span>
            )
          )}
        </div>
        {/* Opponent board — full size, no crop. Read-only (no onSlotTap passed),
            placed cards only (server masks the opponent's private state). */}
        <OfcBoard board={oppSeat ? publicState.boards[oppSeat] : emptyBoard} />
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MIDDLE: Clocks + turn indicator + commit hash                     */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-bg-secondary px-3 py-2">
        {/* Clock pills */}
        <div className="flex items-center gap-2">
          {/* My clock */}
          <span
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-mono font-semibold tabular-nums ${
              isMyTurn
                ? 'bg-brand/20 text-brand'
                : 'bg-bg-tertiary text-fg-muted'
            }`}
          >
            <span className="text-[10px] font-sans font-bold uppercase tracking-wide opacity-70">{t('match.me')}</span>
            {formatClock(mySeat ? displayClocks[mySeat] : 0)}
          </span>
          {/* Opponent clock */}
          <span
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-mono font-semibold tabular-nums ${
              !isMyTurn
                ? 'bg-warning/15 text-warning'
                : 'bg-bg-tertiary text-fg-muted'
            }`}
          >
            <span className="text-[10px] font-sans font-bold uppercase tracking-wide opacity-70">{t('match.opp')}</span>
            {formatClock(oppSeat ? displayClocks[oppSeat] : 0)}
          </span>
        </div>

        {/* Turn indicator */}
        <span
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${
            !m.connected
              ? 'bg-danger/15 text-danger'
              : isMyTurn
                ? 'bg-brand/20 text-brand'
                : 'bg-bg-tertiary text-fg-muted'
          }`}
        >
          <span
            className={`h-2 w-2 rounded-full ${
              !m.connected
                ? 'bg-danger'
                : isMyTurn
                  ? 'animate-pulse bg-brand'
                  : 'bg-fg-muted'
            }`}
          />
          {!m.connected
            ? t('match.connecting')
            : isMyTurn
              ? t('match.yourTurn')
              : t('match.opponentTurn')}
        </span>

        {/* Round + commit hash */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-fg-muted">
            {t('match.round', { n: publicState.round + 1 })}
          </span>
          {shortHash && (
            <span className="rounded-md bg-bg-tertiary px-2 py-0.5 font-mono text-[10px] text-fg-muted">
              #{shortHash}
            </span>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* BOTTOM: My board + draw tray + action buttons                     */}
      {/* ══════════════════════════════════════════════════════════════════ */}

      {/* Fantasyland prompt — set the whole board (3/5/5) in one go */}
      {myFl && showPlaceUI && (
        <div className="rounded-xl bg-brand/15 px-3 py-2 text-center text-sm font-semibold text-brand">
          ✦ {t('hand.flPlacePrompt')}
        </div>
      )}

      {/* My board */}
      <div className={`rounded-xl transition-all ${showPlaceUI ? 'ring-2 ring-brand' : 'ring-1 ring-border'}`}>
        <OfcBoard
          board={myDisplayBoard}
          pendingKeys={placedKeys}
          pickedKey={pickedBoardKey ?? undefined}
          onSlotTap={showPlaceUI ? (row, index) => {
            const tapped = myDisplayBoard[row][index];
            const tappedKey = tapped ? cardKey(tapped) : null;
            const tappedPending = tappedKey ? placedKeys.has(tappedKey) : false;

            // 1) A draw card is selected in the tray → place it on this row.
            if (placement.selectedIndex !== null) {
              haptics.impact('light');
              placement.placeTo(row);
              setPickedBoardKey(null);
              return;
            }

            // 2) A board card is picked up → act relative to it.
            if (pickedBoardKey) {
              const picked = placement.pending.find((p) => cardKey(p.card) === pickedBoardKey);
              if (!picked) { setPickedBoardKey(null); return; }

              // 2a) Re-tap the SAME picked card → send it back to the tray.
              if (tappedKey === pickedBoardKey) {
                haptics.impact('light');
                placement.unplace(picked.card);
                setPickedBoardKey(null);
                return;
              }

              // Swap / move only WITHIN the picked card's row (cosmetic reorder).
              if (picked.row === row) {
                const current = rowOrder[row].length ? rowOrder[row] : myDisplayBoard[row].map(cardKey);
                if (tapped && tappedKey) {
                  // 2b) Tap ANY other card in the row (committed OR pending) → swap.
                  const next = [...current];
                  const i = next.indexOf(pickedBoardKey);
                  const j = next.indexOf(tappedKey);
                  if (i !== -1 && j !== -1) [next[i], next[j]] = [next[j], next[i]];
                  haptics.impact('light');
                  setRowOrder((prev) => ({ ...prev, [row]: next }));
                  setPickedBoardKey(null);
                  return;
                }
                // 2c) Tap an empty slot in the row → move picked to the end.
                haptics.impact('light');
                setRowOrder((prev) => ({
                  ...prev,
                  [row]: [...current.filter((k) => k !== pickedBoardKey), pickedBoardKey],
                }));
                setPickedBoardKey(null);
                return;
              }
              // Different row → MOVE the picked card to that row (capacity permitting).
              // This re-assigns its row this hand (changes the hand — allowed, it's
              // not yet submitted). If the row is full, keep it picked (no-op).
              const capacities = { front: 3, middle: 5, back: 5 } as const;
              if (myDisplayBoard[row].length < capacities[row]) {
                haptics.impact('light');
                placement.moveTo(picked.card, row);
                setPickedBoardKey(null);
              }
              return;
            }

            // 3) Nothing selected → tap a card placed this hand to pick it up.
            if (tappedPending && tappedKey) {
              haptics.impact('light');
              setPickedBoardKey(tappedKey);
            }
          } : undefined}
          highlightRows={
            showPlaceUI && placement.selectedIndex !== null
              ? (['front', 'middle', 'back'] as const).filter(
                  (row) => {
                    // Show rows that are not yet full as legal targets (counting
                    // the optimistic pending cards already shown on the board).
                    const capacities = { front: 3, middle: 5, back: 5 } as const;
                    return myDisplayBoard[row].length < capacities[row];
                  },
                )
              : []
          }
        />
      </div>

      {/* Draw tray + placement UI (only when it's my turn, phase=placing) */}
      {showPlaceUI ? (
        <div className="flex flex-col gap-3">
          <DrawTray
            cards={remainingDraw.map((r) => r.card)}
            selectedIndex={selectedTrayPos === -1 ? null : selectedTrayPos}
            discarding={placement.discard !== null || (myFl && placement.isComplete)}
            onSelect={(pos) => {
              const r = remainingDraw[pos];
              if (!r) return;
              haptics.impact('light');
              setPickedBoardKey(null);
              placement.select(r.idx);
            }}
          />

          {/* Confirm button — enabled when placement is complete */}
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              disabled={!placement.isComplete}
              onClick={handleConfirm}
              className="rounded-xl bg-brand px-8 py-2.5 text-sm font-semibold text-brand-fg disabled:opacity-40"
            >
              {t('match.confirm')}
            </button>
          </div>
        </div>
      ) : (
        /* Waiting for opponent or non-placing phase */
        !isMyTurn && isPlacingPhase && (
          <div className="flex items-center justify-center py-3">
            <span className="rounded-xl bg-bg-secondary px-4 py-2 text-sm font-semibold text-fg-muted">
              {t('match.waitingForOpponent')}
            </span>
          </div>
        )
      )}

      {/* Resign button — available until the set (match) is over */}
      {!m.matchResult && (
        <div className="flex items-center justify-center pt-1">
          <button
            type="button"
            onClick={() => {
              haptics.impact('medium');
              m.resign();
            }}
            className="text-xs font-medium text-danger"
          >
            {t('match.resign')}
          </button>
        </div>
      )}
    </div>
  );
}
